// WS5 PR4a — Virtual touch joystick (mobile FP).
//
// Componente puro DOM (SVG) + handler touch dedicato che pubblica un
// WalkTouchInput a ogni move. NO dipendenze esterne (nipplejs rifiutato
// per non aggiungere superficie di manutenzione).
//
// Convenzione cardinale (coerente con `forwardVector` in movement.ts):
// dito sopra al centro (touchY < centerY) → forward positivo (camminata
// in avanti rispetto allo yaw corrente). Dito a destra (touchX > centerX)
// → strafe positivo (laterale destro).
//
// Touch identifier tracking: il joystick ricorda il `Touch.identifier`
// con cui ha catturato lo start; ogni `touchmove`/`touchend` filtra solo
// quel dito (i restanti dita sul canvas appartengono al drag-rotate o
// alla pinch in `touch-controls.ts`).
//
// Cleanup contract: `dispose()` rimuove DOM + tutti i listener +
// pubblica un ultimo `onUpdate(null)` (lo consumer azzera lo state).

import {
  JOYSTICK_DEADZONE,
  JOYSTICK_RADIUS_PX,
  JOYSTICK_RUN_THRESHOLD,
  type WalkTouchInput,
} from './types.js';
import { computeJoystickInput } from './movement.js';

export interface JoystickControllerOptions {
  readonly onUpdate: (input: WalkTouchInput | null) => void;
  // Override testabile dei probe geometrici (default JOYSTICK_RADIUS_PX).
  readonly radiusPx?: number;
  // Override deadzone/run threshold per test (default da types.ts).
  readonly deadzone?: number;
  readonly runThreshold?: number;
  // SVG namespace override (default `http://www.w3.org/2000/svg`).
  readonly svgNs?: string;
  // Etichetta aria opzionale (i18n lato chiamante).
  readonly ariaLabel?: string;
}

export interface JoystickController {
  readonly root: HTMLElement;
  readonly getRect: () => DOMRect | null;
  dispose(): void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// Snapshot del touch attivo (identifier + posizione centro). Il centro
// e' fissato al touchstart (NON segue il dito), cosi' un piccolo overshoot
// non sposta tutta la base; il dito puo' uscire dal raggio (clampato a 1).
interface ActiveTouch {
  readonly identifier: number;
  readonly centerX: number;
  readonly centerY: number;
}

export function createJoystickController(
  parent: HTMLElement,
  options: JoystickControllerOptions,
): JoystickController {
  const radius = options.radiusPx ?? JOYSTICK_RADIUS_PX;
  const deadzone = options.deadzone ?? JOYSTICK_DEADZONE;
  const runThreshold = options.runThreshold ?? JOYSTICK_RUN_THRESHOLD;
  const svgNs = options.svgNs ?? SVG_NS;
  const doc = parent.ownerDocument ?? document;

  const root = doc.createElement('div');
  root.classList.add('viewer-ui-interactive');
  root.setAttribute('role', 'group');
  if (options.ariaLabel !== undefined) {
    root.setAttribute('aria-label', options.ariaLabel);
  }
  // touch-action:none → blocca scroll/zoom nativo del browser sull'area
  // joystick. pointer-events:auto → riceve i tocchi (parent layer canvas
  // ha pointer-events:none in alcune surface).
  root.style.cssText = [
    'position:fixed',
    'left:calc(env(safe-area-inset-left, 0px) + 1.25rem)',
    'bottom:calc(env(safe-area-inset-bottom, 0px) + 1.25rem)',
    `width:${radius * 2}px`,
    `height:${radius * 2}px`,
    'z-index:12',
    'touch-action:none',
    'pointer-events:auto',
    'user-select:none',
    '-webkit-user-select:none',
    '-webkit-touch-callout:none',
  ].join(';');

  const svg = doc.createElementNS(svgNs, 'svg');
  svg.setAttribute('width', String(radius * 2));
  svg.setAttribute('height', String(radius * 2));
  svg.setAttribute('viewBox', `0 0 ${radius * 2} ${radius * 2}`);
  svg.setAttribute('aria-hidden', 'true');
  // Base anello + stick centrato.
  const base = doc.createElementNS(svgNs, 'circle');
  base.setAttribute('cx', String(radius));
  base.setAttribute('cy', String(radius));
  base.setAttribute('r', String(radius - 4));
  base.setAttribute('fill', 'rgba(15,15,17,0.42)');
  base.setAttribute('stroke', 'rgba(255,255,255,0.32)');
  base.setAttribute('stroke-width', '2');
  svg.appendChild(base);
  const stick = doc.createElementNS(svgNs, 'circle');
  stick.setAttribute('cx', String(radius));
  stick.setAttribute('cy', String(radius));
  stick.setAttribute('r', String(radius * 0.38));
  stick.setAttribute('fill', 'rgba(255,255,255,0.78)');
  stick.setAttribute('stroke', 'rgba(15,15,17,0.6)');
  stick.setAttribute('stroke-width', '2');
  svg.appendChild(stick);
  root.appendChild(svg);
  parent.appendChild(root);

  let active: ActiveTouch | null = null;
  let lastInput: WalkTouchInput | null = null;

  const setStickOffset = (dx: number, dy: number): void => {
    stick.setAttribute('cx', String(radius + dx));
    stick.setAttribute('cy', String(radius + dy));
  };

  const resetStick = (): void => {
    setStickOffset(0, 0);
  };

  const publish = (input: WalkTouchInput | null): void => {
    lastInput = input;
    try {
      options.onUpdate(input);
    } catch (err) {
      console.warn('[joystick] onUpdate threw:', err);
    }
  };

  const onTouchStart = (ev: TouchEvent): void => {
    if (active !== null) return; // ignora dita aggiuntive sul joystick
    const touch = ev.changedTouches.item(0);
    if (touch === null) return;
    // Il centro joystick e' al centro DOM dell'elemento root (non al
    // touchX/Y: il dito puo' atterrare ai bordi e proseguire dall'esterno).
    const rect = root.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    active = { identifier: touch.identifier, centerX, centerY };
    // Bloccare scroll/zoom nativo: necessario passive:false sul listener
    // perche' preventDefault dentro touchmove altrimenti non bloccherebbe.
    ev.preventDefault();
    const input = computeJoystickInput(
      touch.clientX,
      touch.clientY,
      centerX,
      centerY,
      radius,
      deadzone,
      runThreshold,
    );
    const dx = (touch.clientX - centerX) * (input.forward !== 0 || input.strafe !== 0 ? 1 : 0);
    const dy = (touch.clientY - centerY) * (input.forward !== 0 || input.strafe !== 0 ? 1 : 0);
    // Clamp visivo dello stick al raggio.
    const stickLen = Math.hypot(dx, dy);
    if (stickLen > radius) {
      setStickOffset((dx / stickLen) * radius, (dy / stickLen) * radius);
    } else {
      setStickOffset(dx, dy);
    }
    publish(input);
  };

  const onTouchMove = (ev: TouchEvent): void => {
    if (active === null) return;
    let touch: Touch | null = null;
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches.item(i);
      if (t !== null && t.identifier === active.identifier) {
        touch = t;
        break;
      }
    }
    if (touch === null) return;
    ev.preventDefault();
    const input = computeJoystickInput(
      touch.clientX,
      touch.clientY,
      active.centerX,
      active.centerY,
      radius,
      deadzone,
      runThreshold,
    );
    // Codex iter2 Q13 (WARNING): se input e' deadzone (forward=strafe=0)
    // riporta visivamente lo stick al centro, evitando drift mentre il
    // dito si muove ancora dentro la deadzone. Senza questo guard lo
    // stick "scivola" sotto il pollice anche se logicamente camminata=0.
    if (input.forward === 0 && input.strafe === 0) {
      setStickOffset(0, 0);
    } else {
      const rawDx = touch.clientX - active.centerX;
      const rawDy = touch.clientY - active.centerY;
      const stickLen = Math.hypot(rawDx, rawDy);
      if (stickLen > radius) {
        setStickOffset((rawDx / stickLen) * radius, (rawDy / stickLen) * radius);
      } else {
        setStickOffset(rawDx, rawDy);
      }
    }
    publish(input);
  };

  const onTouchEnd = (ev: TouchEvent): void => {
    if (active === null) return;
    let endedOurs = false;
    for (let i = 0; i < ev.changedTouches.length; i++) {
      const t = ev.changedTouches.item(i);
      if (t !== null && t.identifier === active.identifier) {
        endedOurs = true;
        break;
      }
    }
    if (!endedOurs) return;
    ev.preventDefault();
    active = null;
    resetStick();
    publish(null);
  };

  // passive:false per touchstart/move/end → preventDefault valido (blocca
  // scroll del documento + zoom doppio-tap nativo).
  root.addEventListener('touchstart', onTouchStart, { passive: false });
  root.addEventListener('touchmove', onTouchMove, { passive: false });
  root.addEventListener('touchend', onTouchEnd, { passive: false });
  root.addEventListener('touchcancel', onTouchEnd, { passive: false });

  return {
    root,
    getRect(): DOMRect | null {
      if (!root.isConnected) return null;
      return root.getBoundingClientRect();
    },
    dispose(): void {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('touchcancel', onTouchEnd);
      active = null;
      if (root.parentElement !== null) {
        root.parentElement.removeChild(root);
      }
      // Final null push se mai dovesse essere ancora attivo.
      if (lastInput !== null) {
        publish(null);
      }
    },
  };
}
